import { computed } from "vue";
import { useStore } from "vuex";
import { UIContent, UIContentDefinition, UIContentValue } from "./uiContent";

type KeysWithDefaultOfType<T, TValue> = {
    [K in keyof T]: T[K] extends { default: TValue } ? K : never
}[keyof T];

type VariantValue<TDefinition> =
    TDefinition extends { variants: infer TVariants }
        ? TVariants extends object
            ? TVariants[keyof TVariants]
            : never
        : never;

type KeysWithVariantsOfType<T, TValue> = {
    [K in keyof T]:
        T[K] extends { variants: object }
            ? VariantValue<T[K]> extends TValue
                ? K
                : never
            : never;
}[keyof T];

type VariantKeyFor<TKey extends keyof UIContent> =
    UIContent[TKey] extends { variants: infer TVariants }
        ? Extract<keyof TVariants, string>
        : never;

type UIContentTextKey = KeysWithDefaultOfType<UIContent, string>;
type UIContentTextArrayKey = KeysWithDefaultOfType<UIContent, string[]>;
type UIContentBoolKey = KeysWithDefaultOfType<UIContent, boolean>;
type UIContentVariantTextKey = KeysWithVariantsOfType<UIContent, string>;
type UIContentVariantTextArrayKey = KeysWithVariantsOfType<UIContent, string[]>;
type UIContentVariantBoolKey = KeysWithVariantsOfType<UIContent, boolean>;
type TemplateValues = Record<string, string | number>;

export function useUIContent() {
    const store = useStore();
    const uiContent = computed<UIContent | undefined>(() => store.state.configuration?.uiContent);

    function text<TKey extends UIContentTextKey>(key: TKey): string;

    function text<TKey extends UIContentVariantTextKey>(key: TKey, variant: VariantKeyFor<TKey>): string;

    function text(key: UIContentTextKey | UIContentVariantTextKey, variant?: string): string {
        return getContentValue<string>(key, variant) ?? "";
    }

    function textArray<TKey extends UIContentTextArrayKey>(key: TKey): string[];

    function textArray<TKey extends UIContentVariantTextArrayKey>(key: TKey, variant: VariantKeyFor<TKey>): string[];

    function textArray(key: UIContentTextArrayKey | UIContentVariantTextArrayKey, variant?: string): string[] {
        return getContentValue<string[]>(key, variant) ?? [];
    }

    function html<TKey extends UIContentTextKey>(key: TKey): string;

    function html<TKey extends UIContentVariantTextKey>(key: TKey, variant: VariantKeyFor<TKey>): string;

    function html(key: UIContentTextKey | UIContentVariantTextKey, variant?: string): string {
        const value = getContentValue<string>(key, variant) ?? "";
        return sanitiseHtml(value);
    }

    function htmlArray<TKey extends UIContentTextArrayKey>(key: TKey): string[];

    function htmlArray<TKey extends UIContentVariantTextArrayKey>(key: TKey, variant: VariantKeyFor<TKey>): string[];

    function htmlArray(key: UIContentTextArrayKey | UIContentVariantTextArrayKey, variant?: string): string[] {
        const values = getContentValue<string[]>(key, variant) ?? [];
        return values.map(v => sanitiseHtml(v));
    }

    function templateText<TKey extends UIContentTextKey>(key: TKey, values: TemplateValues): string;

    function templateText<TKey extends UIContentVariantTextKey>(key: TKey, variant: VariantKeyFor<TKey>, values: TemplateValues): string;

    function templateText(key: UIContentTextKey | UIContentVariantTextKey, variantOrValues: string | TemplateValues, values?: TemplateValues): string {
        if (typeof variantOrValues === "string") {
            const template = getContentValue<string>(key, variantOrValues) ?? "";
            return replaceTemplateKeys(template, values ?? {});
        }

        const template = getContentValue<string>(key) ?? "";
        return replaceTemplateKeys(template, variantOrValues);
    }

    function templateTextArray<TKey extends UIContentTextArrayKey>(key: TKey, values: TemplateValues): string[];

    function templateTextArray<TKey extends UIContentVariantTextArrayKey>(key: TKey, variant: VariantKeyFor<TKey>, values: TemplateValues): string[];

    function templateTextArray(key: UIContentTextArrayKey | UIContentVariantTextArrayKey, variantOrValues: string | TemplateValues, values?: TemplateValues): string[] {
        let templates: string[];
        let templateValues: TemplateValues;

        if (typeof variantOrValues === "string") {
            templates = getContentValue<string[]>(key, variantOrValues) ?? [];
            templateValues = values ?? {};
        }
        else {
            templates = getContentValue<string[]>(key) ?? [];
            templateValues = variantOrValues;
        }

        return templates.map(template =>
            replaceTemplateKeys(template, templateValues)
        );
    }

    function templateHtml<TKey extends UIContentTextKey>(key: TKey, values: TemplateValues): string;

    function templateHtml<TKey extends UIContentVariantTextKey>(key: TKey, variant: VariantKeyFor<TKey>, values: TemplateValues): string;

    function templateHtml(key: UIContentTextKey | UIContentVariantTextKey, variantOrValues: string | TemplateValues, values?: TemplateValues): string {
        if (typeof variantOrValues === "string") {
            const template = getContentValue<string>(key, variantOrValues) ?? "";
            return replaceTemplateKeys(template, values ?? {});
        }

        const template = getContentValue<string>(key) ?? "";
        const updated = replaceTemplateKeys(template, variantOrValues);
        return sanitiseHtml(updated);
    }

    function templateHtmlArray<TKey extends UIContentTextArrayKey>(key: TKey, values: TemplateValues): string[];

    function templateHtmlArray<TKey extends UIContentVariantTextArrayKey>(key: TKey, variant: VariantKeyFor<TKey>, values: TemplateValues): string[];

    function templateHtmlArray(key: UIContentTextArrayKey | UIContentVariantTextArrayKey, variantOrValues: string | TemplateValues, values?: TemplateValues): string[] {
        let templates: string[];
        let templateValues: TemplateValues;

        if (typeof variantOrValues === "string") {
            templates = getContentValue<string[]>(key, variantOrValues) ?? [];
            templateValues = values ?? {};
        }
        else {
            templates = getContentValue<string[]>(key) ?? [];
            templateValues = variantOrValues;
        }

        const updated = templates.map(template =>
            replaceTemplateKeys(template, templateValues)
        );

        return updated.map(u => sanitiseHtml(u));
    }

    function bool<TKey extends UIContentBoolKey>(key: TKey): boolean;

    function bool<TKey extends UIContentVariantBoolKey>(key: TKey, variant: VariantKeyFor<TKey>): boolean;

    function bool(key: UIContentBoolKey | UIContentVariantBoolKey, variant?: string): boolean {
        return getContentValue<boolean>(key, variant) ?? false;
    }
    
    function getContentValue<TValue extends UIContentValue>(key: keyof UIContent, variant?: string): TValue | undefined {
        const definition = uiContent.value?.[key] as
            | UIContentDefinition<TValue>
            | undefined;

        if (variant !== undefined) {
            return definition?.variants?.[variant];
        }

        return definition?.default;
    }

    function replaceTemplateKeys(template: string, values: TemplateValues): string {
        for (const [name, replacement] of Object.entries(values)) {
            template = template.replaceAll(`{${name}}`, String(replacement));
        }

        return template;
    }

    function sanitiseHtml(value: string): string {
        // Add sanitisation before allowing business users content management capability.
        return value;
    }

    return {
        uiContent,
        text,
        textArray,
        html,
        htmlArray,
        templateText,
        templateTextArray,
        templateHtml,
        templateHtmlArray,
        bool
    };
}
